import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analyzeHand, suggestArrangement } from '../utils/suggestionEngine';
import './PracticeSession.css';

const SUITS = ['hearts', 'diamonds', 'clubs', 'spades'];
const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
const SUIT_SYM = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' };

function buildDeck() {
  const deck = [];
  for (let d = 0; d < 2; d++) {
    SUITS.forEach((s) => RANKS.forEach((r) => {
      deck.push({ id: `${d}_${r}_${s}`, suit: s, rank: r, isJoker: false, isRed: s === 'hearts' || s === 'diamonds' });
    }));
  }
  for (let i = 0; i < 4; i++) deck.push({ id: `joker_${i}`, suit: 'joker', rank: 'Joker', isJoker: true, isRed: i < 2 });
  return deck;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

const CardFace = ({ card, selected, onClick }) => {
  const red = card.isJoker ? false : (card.suit === 'hearts' || card.suit === 'diamonds');
  const sym = card.isJoker ? '★' : (SUIT_SYM[card.suit] || '');
  const label = card.isJoker ? 'JOK' : card.rank;
  return (
    <div className={`ps-card ${red ? 'red' : 'black'} ${selected ? 'selected' : ''}`} onClick={onClick}>
      <div className="ps-card-tl">{label}{sym}</div>
      <div className="ps-card-mid">{sym}</div>
    </div>
  );
};

const PracticeSessionPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [game, setGame] = useState(null);
  const [selected, setSelected] = useState(null);
  const [hint, setHint] = useState(null);
  const [message, setMessage] = useState('');
  const aiTimer = useRef(null);

  const clone = (g) => JSON.parse(JSON.stringify(g));

  const startGame = () => {
    const deck = shuffle(buildDeck());
    const players = [
      { id: 'p1', name: currentUser?.username || 'You', isAI: false, cards: deck.splice(0, 13) },
      { id: 'p2', name: 'AI 1', isAI: true, cards: deck.splice(0, 13) },
      { id: 'p3', name: 'AI 2', isAI: true, cards: deck.splice(0, 13) },
      { id: 'p4', name: 'AI 3', isAI: true, cards: deck.splice(0, 13) },
    ];
    const wildJoker = deck[Math.floor(Math.random() * deck.length)];
    const discardPile = [deck.pop()];
    setGame({ players, deck, discardPile, wildJoker, turn: 0, phase: 'playing', winner: null });
    setSelected(null);
    setHint(null);
    setMessage('Game started — your turn. Draw a card to begin.');
  };

  useEffect(() => {
    startGame();
    return () => clearTimeout(aiTimer.current);
    // eslint-disable-next-line
  }, []);

  const me = () => game?.players?.find((p) => !p.isAI);
  const current = () => game?.players?.[game.turn];

  const advanceTurn = (g) => {
    g.turn = (g.turn + 1) % g.players.length;
    setGame(g);
    setSelected(null);
  };

  // AI move: draw from deck, discard the best deadwood.
  const runAI = () => {
    if (!game) return;
    const p = current();
    if (!p || !p.isAI || game.phase !== 'playing') return;
    aiTimer.current = setTimeout(() => {
      const g = clone(game);
      const ai = g.players[g.turn];
      const drawn = g.deck.pop();
      if (drawn) ai.cards.push(drawn);
      const analysis = analyzeHand(ai.cards, g.wildJoker);
      const toDiscard = analysis.bestDiscard || ai.cards[ai.cards.length - 1];
      ai.cards = ai.cards.filter((c) => c.id !== toDiscard.id);
      g.discardPile.push(toDiscard);
      advanceTurn(g);
    }, 900);
  };

  useEffect(() => {
    if (game && game.phase === 'playing' && current()?.isAI) runAI();
    // eslint-disable-next-line
  }, [game?.turn]);

  const draw = (source) => {
    if (!game || !me() || game.phase !== 'playing' || current()?.isAI) return;
    if (source === 'discard' && game.discardPile.length === 0) { setMessage('Discard pile is empty'); return; }
    const g = clone(game);
    const p = g.players.find((x) => !x.isAI);
    const card = source === 'discard' ? g.discardPile.pop() : g.deck.pop();
    if (!card) { setMessage('Deck is empty'); return; }
    p.cards.push(card);
    setMessage(source === 'discard' ? `You drew ${card.isJoker ? 'a Joker' : card.rank + SUIT_SYM[card.suit]} from the discard pile` : 'You drew a card from the deck');
    advanceTurn(g);
  };

  const discard = () => {
    if (!game || !me() || game.phase !== 'playing' || current()?.isAI) return;
    if (!selected) { setMessage('Select a card to discard first'); return; }
    const g = clone(game);
    const p = g.players.find((x) => !x.isAI);
    const card = p.cards.find((c) => c.id === selected);
    if (!card) { setSelected(null); return; }
    p.cards = p.cards.filter((c) => c.id !== selected);
    g.discardPile.push(card);
    setMessage(`You discarded ${card.isJoker ? 'a Joker' : card.rank + SUIT_SYM[card.suit]}`);
    advanceTurn(g);
  };

  const declare = () => {
    if (!game || !me()) return;
    const analysis = analyzeHand(me().cards, game.wildJoker);
    if (analysis.isValid) {
      const g = clone(game);
      g.phase = 'finished';
      g.winner = me().id;
      setGame(g);
      setHint(null);
      setMessage('🎉 Valid declaration — you win!');
    } else {
      setMessage(analysis.hasPureSequence ? 'Not valid yet — every card must be grouped.' : 'Not valid yet — you need at least one pure sequence.');
    }
  };

  const autoArrange = () => {
    if (!game || !me()) return;
    const { arrangedCards } = suggestArrangement(me().cards, game.wildJoker);
    const g = clone(game);
    g.players.find((x) => !x.isAI).cards = arrangedCards;
    setGame(g);
    setSelected(null);
    setMessage('✨ Cards arranged — valid groups first, then leftover cards.');
  };

  const showHint = () => {
    if (!game || !me()) return;
    setHint(analyzeHand(me().cards, game.wildJoker));
  };

  if (!game) return <div className="ps-page"><div className="ps-loading">Starting practice game…</div></div>;

  const human = me();
  const topDiscard = game.discardPile[game.discardPile.length - 1];
  const isMyTurn = game.phase === 'playing' && !current()?.isAI;

  return (
    <div className="ps-page">
      <div className="ps-topbar">
        <button className="ps-btn ghost" onClick={() => navigate('/practice-games')}>← Exit</button>
        <div className="ps-title">Base Practice</div>
        <div className="ps-wild">Wild joker: <CardFace card={game.wildJoker} /></div>
      </div>

      <div className="ps-opponents">
        {game.players.filter((p) => p.isAI).map((p) => {
          const idx = game.players.indexOf(p);
          return (
            <div key={p.id} className={`ps-opp ${game.turn === idx ? 'turn' : ''}`}>
              <div className="ps-opp-name">{p.name}</div>
              <div className="ps-opp-cards">{p.cards.length} cards</div>
            </div>
          );
        })}
      </div>

      <div className="ps-center">
        <div className="ps-pile">
          <div className="ps-pile-label">Deck ({game.deck.length})</div>
          <div className="ps-deck-back" onClick={() => draw('deck')}>🂠</div>
        </div>
        <div className="ps-pile">
          <div className="ps-pile-label">Discard</div>
          {topDiscard ? <CardFace card={topDiscard} onClick={() => draw('discard')} /> : <div className="ps-empty">—</div>}
        </div>
      </div>

      <div className="ps-hand">
        {human.cards.map((c) => (
          <CardFace key={c.id} card={c} selected={selected === c.id} onClick={() => setSelected(selected === c.id ? null : c.id)} />
        ))}
      </div>

      <div className="ps-actions">
        <button className="ps-btn" onClick={() => draw('deck')} disabled={!isMyTurn}>Draw Deck</button>
        <button className="ps-btn" onClick={() => draw('discard')} disabled={!isMyTurn || game.discardPile.length === 0}>Draw Discard</button>
        <button className="ps-btn" onClick={discard} disabled={!isMyTurn || !selected}>Discard</button>
        <button className="ps-btn alt" onClick={autoArrange}>✨ Auto-Arrange</button>
        <button className="ps-btn alt" onClick={showHint}>💡 Hint</button>
        <button className="ps-btn danger" onClick={declare} disabled={!isMyTurn}>Declare</button>
      </div>

      {message && <div className="ps-message">{message}</div>}

      {hint && (
        <div className="ps-hint">
          <div className="ps-hint-head">
            <strong>💡 Suggestions</strong>
            <button className="ps-btn ghost" onClick={() => setHint(null)}>Close</button>
          </div>
          {hint.combinations.length > 0 ? (
            <div className="ps-hint-block"><strong>Combinations:</strong>
              {hint.combinations.map((c, i) => <div key={i}>• {c}</div>)}
            </div>
          ) : <div className="ps-hint-block">No full group found yet — keep drawing.</div>}
          {hint.hints.length > 0 && (
            <div className="ps-hint-block"><strong>Tips:</strong>
              {hint.hints.map((h, i) => <div key={i}>• {h}</div>)}
            </div>
          )}
          {hint.bestDiscard && (
            <div className="ps-hint-block"><strong>Best card to discard:</strong> {hint.bestDiscard.isJoker ? 'Joker' : hint.bestDiscard.rank + SUIT_SYM[hint.bestDiscard.suit]}</div>
          )}
          <div className="ps-hint-block">
            <strong>Status:</strong> {hint.hasPureSequence ? '✅ Pure sequence found' : '❌ No pure sequence yet'} · Deadwood {hint.deadwoodPoints} pts · {hint.isValid ? '✅ Valid declaration' : 'not valid yet'}
          </div>
        </div>
      )}

      {game.phase === 'finished' && (
        <div className="ps-win">🏆 {game.winner === human.id ? 'You win!' : `${game.players.find((p) => p.id === game.winner)?.name} wins`}</div>
      )}
    </div>
  );
};

export default PracticeSessionPage;
